import { useEffect, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import { CheckIcon, UndoIcon } from "../components/Icons";
import ItemCard from "../components/ItemCard";
import Picture from "../components/Picture";
import ui from "../components/ui.module.css";
import useSentBack from "../hooks/useSentBack";

const MAX_DIGITS = 2;

// Counted things often share one picture. Repeated names get their place,
// so each can still be told apart by name.
const namesOf = (items) => {
  const base = items.map((item) => item.label || item.asset?.alt || "Picture");
  return base.map((name, i) =>
    base.filter((n) => n === name).length > 1 ? `${name}, ${i + 1}` : name
  );
};

// A guess: the authored number choices, or a large keypad when there are none.
// A guess is never right or wrong.
const Estimate = ({ options, onChoose, saving }) => {
  const [typed, setTyped] = useState("");
  if (options) {
    return (
      <div className={ui.numbers} role="group" aria-label="Your guess">
        {options.map((n) => (
          <button
            key={n}
            type="button"
            className={ui.number}
            onClick={() => onChoose(n)}
            disabled={saving}
          >
            {n}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className={ui.keypad} role="group" aria-label="Your guess">
      <output className={ui.keypadShow} aria-live="polite">
        {typed || "?"}
      </output>
      <div className={ui.numbers}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((n) => (
          <button
            key={n}
            type="button"
            className={ui.number}
            onClick={() => setTyped((t) => (t.length < MAX_DIGITS ? `${t === "0" ? "" : t}${n}` : t))}
            disabled={saving}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          className={ui.number}
          onClick={() => setTyped((t) => t.slice(0, -1))}
          disabled={saving || !typed}
          aria-label="Rub out"
        >
          <UndoIcon size={28} />
        </button>
      </div>
      <button
        type="button"
        className={`${ui.button} ${ui.large} ${ui.primary}`}
        onClick={() => onChoose(Number(typed))}
        disabled={saving || !typed}
      >
        <CheckIcon size={24} />
        {saving ? "Saving…" : "That's my guess"}
      </button>
    </div>
  );
};

// One counting primitive with three modes (contract §5.3):
//   tap_count            tap each thing once; each tap shows its number.
//   make_set             tap things to move them into the set, and back out.
//   estimate_then_count  guess first (kept, never judged), then tap to count.
// Items keep their own values however the screen is laid out, so a retry
// returns to the same things. The count is handed in as it is; the gateway
// judges it and policy decides what happens next (Handoff 007 §3).
const CountingStep = ({ step, saved, verdict, onDone, onEstimate, saving, readOnly = false }) => {
  const names = namesOf(step.items);
  const guessing = step.mode === "estimate_then_count";
  const estimate = guessing && Number.isInteger(saved?.estimate) ? saved.estimate : null;
  const [counted, setCounted] = useState(() =>
    readOnly && Array.isArray(saved?.selected) ? saved.selected : []
  ); // item values, in the order tapped
  // "sent" once handed in; "wrong" while a sent-back count is shown.
  const [result, setResult] = useState(readOnly ? "sent" : null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useSentBack(verdict, () => setResult("wrong"));

  const locked = saving || result === "sent";
  const makingSet = step.mode === "make_set";

  const tap = (value) => {
    if (locked) return;
    setResult(null);
    setCounted((current) =>
      current.includes(value)
        ? makingSet
          ? current.filter((v) => v !== value)
          : current // counted once, however often it is tapped
        : [...current, value]
    );
  };

  const check = () => {
    if (locked) return;
    const count = counted.length;
    const selected = step.items.map((i) => i.value).filter((v) => counted.includes(v));
    setResult("sent");
    onDone({ count, selected, ...(guessing ? { estimate } : {}) });
  };

  if (guessing && estimate === null) {
    return (
      <>
        <Prompt step={step} />
        <ul className={ui.countItems} aria-label="Things to count">
          {step.items.map((item, index) => (
            <li key={item.value} className={`${ui.choice} ${ui.countCard} ${ui.still}`}>
              <Picture item={item} />
              <span className={ui.visuallyHidden}>{names[index]}</span>
            </li>
          ))}
        </ul>
        <p className={ui.subline}>How many do you think?</p>
        <Estimate
          options={step.numberOptions}
          onChoose={(n) => onEstimate({ estimate: n })}
          saving={saving}
        />
      </>
    );
  }

  const card = (item) => {
    const index = step.items.indexOf(item);
    const position = counted.indexOf(item.value);
    return (
      <li key={item.value}>
        <ItemCard
          item={item}
          className={[
            ui.choice,
            ui.countCard,
            position >= 0 && ui.counted,
            verdict?.kind === "right" && position >= 0 && ui.choiceRight,
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => tap(item.value)}
          aria-pressed={position >= 0}
          aria-label={
            position >= 0
              ? `${names[index]}, ${makingSet ? "in the set" : `counted ${position + 1}`}`
              : names[index]
          }
          disabled={saving}
        >
          {position >= 0 && !makingSet && (
            <span className={ui.slotNumber} aria-hidden="true">
              {position + 1}
            </span>
          )}
          {position >= 0 && makingSet && (
            <CheckIcon className={ui.countTick} size={32} />
          )}
          <Picture item={item} />
        </ItemCard>
      </li>
    );
  };

  const inSet = step.items.filter((i) => counted.includes(i.value));
  const outside = step.items.filter((i) => !counted.includes(i.value));

  return (
    <>
      <Prompt step={step} />
      {guessing && <p className={ui.notice}>You guessed {estimate}</p>}

      {makingSet ? (
        <>
          <section className={ui.set} aria-label="Your set">
            <ul className={ui.countItems}>{inSet.map(card)}</ul>
          </section>
          <ul className={ui.countItems}>{outside.map(card)}</ul>
        </>
      ) : (
        <ul className={`${ui.countItems} ${result === "wrong" ? ui.shake : ""}`}>
          {step.items.map(card)}
        </ul>
      )}

      <p className={ui.tally} aria-live="polite">
        <span className={ui.visuallyHidden}>Counted: </span>
        {counted.length}
      </p>

      <div className={ui.toolButtons}>
        {!makingSet && (
          <button
            type="button"
            className={`${ui.button} ${ui.large} ${ui.secondary}`}
            onClick={() => {
              setCounted([]);
              setResult(null);
            }}
            disabled={locked || counted.length === 0}
          >
            <UndoIcon size={24} />
            Start again
          </button>
        )}
        <button
          type="button"
          className={`${ui.button} ${ui.large} ${ui.primary}`}
          onClick={check}
          disabled={locked}
        >
          <CheckIcon size={24} />
          {saving ? "Saving…" : "Done"}
        </button>
      </div>
    </>
  );
};

export default CountingStep;
